import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SignInForm } from '@/components/auth/SignInForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { SignUpSuccess } from '@/components/auth/SignUpSuccess';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { supabase } from "@/integrations/supabase/client";
import { House } from 'lucide-react';

const Auth = () => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isProcessingRecovery, setIsProcessingRecovery] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, handleReturnUrl } = useAuth();
  const { currentCommunity } = useCommunity();

  const isSignUpMode = searchParams.get('mode') === 'signup';

  const getReturnUrl = () => {
    const returnTo = searchParams.get('returnTo');
    
    if (returnTo) {
      try {
        return decodeURIComponent(returnTo);
      } catch (e) {
        console.error('Error decoding returnTo URL:', e);
      }
    }
    
    return '/';
  };

  useEffect(() => {
    const handleRecoverySession = async () => {
      const hash = window.location.hash;
      console.log("Current URL hash:", hash);
      
      if (hash && hash !== '') {
        const hashParams = new URLSearchParams(hash.substring(1));
        const type = hashParams.get('type');
        
        if (type === 'recovery') {
          console.log('[Auth] Processing recovery session...');
          setIsProcessingRecovery(true);
          
          try {
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (error) {
              console.error('[Auth] Recovery session error:', error);
              navigate('/auth/request-reset-password', { replace: true });
              return;
            }
            
            if (session && session.user) {
              console.log('[Auth] Valid recovery session found, redirecting to reset password');
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
              navigate('/auth/reset-password', { replace: true });
              return;
            } else {
              console.error('[Auth] No valid recovery session found');
              navigate('/auth/request-reset-password', { replace: true });
              return;
            }
          } catch (error) {
            console.error('[Auth] Error processing recovery session:', error);
            navigate('/auth/request-reset-password', { replace: true });
            return;
          } finally {
            setIsProcessingRecovery(false);
          }
        }
      }

      if (!isProcessingRecovery) {
        const returnUrl = getReturnUrl();
        console.log('[Auth] Setting return URL:', returnUrl);
        handleReturnUrl(returnUrl);
        
        if (user) {
          navigate(returnUrl);
        }
      }
    };

    handleRecoverySession();
  }, [user, navigate, location.search, handleReturnUrl, isProcessingRecovery]);

  if (isProcessingRecovery) {
    return (
      <motion.div 
        className="flex items-center justify-center min-h-screen px-4 py-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Processing...</CardTitle>
            <CardDescription>
              Please wait while we process your password reset request.
            </CardDescription>
          </CardHeader>
          <div className="flex justify-center p-6">
            <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[42%_58%] relative">

      {/* Left Panel - Brand Section */}
      <div className="hidden md:flex bg-[hsl(var(--primary)/0.03)] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-24 h-24 bg-primary/6 rounded-full blur-2xl"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                {isSignUpMode ? 'Join' : 'Sign in to'}
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
                  {currentCommunity?.name}
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-sm">
                {isSignUpMode 
                  ? 'Create your account and connect with your local community.' 
                  : 'Connect with your local community and discover amazing services near you.'
                }
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md mx-auto">
          <AnimatePresence mode="wait">
            {showSuccessMessage ? (
              <SignUpSuccess />
            ) : (
              <motion.div
                key="auth-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="rounded-2xl shadow-lg border bg-card">
                  <div className="px-8 pb-8 pt-8">
                    <AnimatePresence mode="wait">
                      {isSignUpMode ? (
                        <motion.div
                          key="signup"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <SignUpForm 
                            returnTo={getReturnUrl()} 
                            onSignUpSuccess={() => setShowSuccessMessage(true)} 
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="signin"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <SignInForm returnTo={getReturnUrl()} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Auth;
